import { Construction } from 'lucide-react'
import { cn } from '@/lib/cn'
import { EmptyState } from '@/components/ui/StateViews'

export interface ComingSoonProps {
  /**
   * The thing that is not built yet, in the user's words — "Time Settings", "Holidays". Always
   * rendered, so the placeholder is NAMED (see below).
   */
  feature: string
  /**
   * One sentence on what is coming. Optional: when omitted, a line naming `feature` is shown, so the
   * panel never degrades to a bare "Coming soon" that says nothing about what.
   */
  description?: string
  /** Tighter padding for use inside a drawer or a small panel rather than a full tab/page body. */
  compact?: boolean
  className?: string
}

/**
 * The placeholder for a screen, tab or panel that is planned but not built yet.
 *
 * ## Built on `EmptyState`, deliberately not a new visual style
 * It reuses `EmptyState`'s shell — the tiled icon, `title-md` heading and muted supporting line —
 * inside the same bordered, muted surface the Time module's hand-rolled placeholders already used.
 * So it looks like the rest of the app's empty/placeholder states, not like a one-off.
 *
 * ## But it must never READ as an empty state
 * `TimeOffPage` recorded why when it built its own placeholder: *"an empty state would claim there
 * is no data, which is the one thing a screen must not say while it does not know."* "No holidays
 * yet" and "Holidays is coming soon" are different claims. So the copy here only ever says
 * "Coming soon" and names the feature — it never says there is nothing.
 *
 * ## Why this exists
 * Replaces several copies of a bare `<Text>Not yet built.</Text>` line (the Time & Attendance
 * "Enrollment" and "Time Settings" tabs rendered exactly that, unstyled, as a whole tab body) and the
 * inlined block in the former `TimeStubPage` (removed when TT-V11 stub screens built its pages). One component, so every unbuilt surface says the same thing the
 * same way, and the day one of them ships only that call site changes.
 */
export function ComingSoon({ feature, description, compact = false, className }: ComingSoonProps) {
  return (
    <section
      aria-label={`${feature}: coming soon`}
      className={cn(
        'rounded-lg border border-border-muted bg-surface-muted px-6',
        compact ? 'py-8' : 'py-12',
        className,
      )}
    >
      <EmptyState
        icon={<Construction size={20} aria-hidden="true" />}
        title="Coming soon"
        description={description ?? `${feature} isn't available yet — it's on the way.`}
        // Full-page stubs carry a sentence or two of "why"; EmptyState's default `max-w-xs` would
        // stack it into a narrow column. Compact (drawer) placements keep the default.
        descriptionClassName={compact ? undefined : 'max-w-prose'}
      />
    </section>
  )
}
